import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { downloadPaymentOrderReceipt } from '../../services/ProfessionalPaymentController';
import { parseApiError } from '../../services/apiError';
import { ReceiptFormsDTO } from '../../dto/ReceiptFormsDTO';
import useNotify from '../../hooks/useNotify';
import useReceiptSummaryDownload from '../../hooks/useReceiptSummaryDownload';
import ReadonlyField from '../ReadonlyField/ReadonlyField';
import AdministrativeFormStateTag from '../AdministrativeFormStateTag/AdministrativeFormStateTag';
import { formatMoney } from '../../utils/money';
import { formatDateOnly } from '../../utils/date';
import './ReceiptFormsDialog.scss';

interface ReceiptFormsDialogProps {
    visible: boolean;
    header: string;
    /** Trae el comprobante y sus pagos del backend (cada módulo con su endpoint, misma fuente). Se llama al abrir. */
    load: () => Promise<ReceiptFormsDTO>;
    /** Nombre del archivo del resumen PDF, con la rendición o liquidación que es. */
    summaryFileName: string;
    onHide: () => void;
}

/**
 * Comprobante interno y todo lo aplicado a él, en solo lectura: la misma ventana para la rendición y para la
 * liquidación. Cada documento muestra lo que le aplicó a este comprobante. Las órdenes de pago se pueden descargar en
 * PDF; las compensaciones (pagos de más, comprobantes de gastos) no son un pago y no tienen PDF. El resumen PDF de la
 * rendición o liquidación sale del comprobante, así existe aunque se haya saldado sin orden de pago.
 */
export default function ReceiptFormsDialog({ visible, header, load, summaryFileName, onHide }: ReceiptFormsDialogProps) {
    const notify = useNotify();
    const summary = useReceiptSummaryDownload();
    const [forms, setForms] = useState<ReceiptFormsDTO | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloadingFormId, setDownloadingFormId] = useState<number | null>(null);
    // La última función de carga, sin que una nueva en cada render vuelva a disparar el pedido
    const loadRef = useRef(load);
    loadRef.current = load;

    useEffect(() => {
        if (!visible) return;
        let cancelled = false;
        setForms(null);
        setLoading(true);
        loadRef.current()
            .then(data => { if (!cancelled) setForms(data); })
            .catch((error: unknown) => {
                if (cancelled) return;
                parseApiError(error); // loguea errorCode y path para cruzarlo con el backend
                notify.error('No se pudieron cargar los comprobantes. Intentá de nuevo.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [visible, notify]);

    async function handleDownload(formId: number, formNumber: number | null) {
        setDownloadingFormId(formId);
        try {
            await downloadPaymentOrderReceipt(formId, formNumber ?? 0);
        } catch (error: unknown) {
            parseApiError(error);
            notify.error('No se pudo descargar la orden de pago. Intentá de nuevo.');
        } finally {
            setDownloadingFormId(null);
        }
    }

    const receipt = forms?.receipt ?? null;
    const payments = forms?.payments ?? [];
    const hasCompensations = payments.some(payment => !payment.paymentOrder);
    const paymentsTitle = hasCompensations
        ? `Pagos y compensaciones (${payments.length})`
        : payments.length === 1 ? 'Orden de pago' : `Órdenes de pago (${payments.length})`;

    return (
        <Dialog
            header={header}
            visible={visible}
            onHide={onHide}
            style={{ width: '560px' }}
            breakpoints={{ '640px': '100vw' }}
            className="md-dialog"
            modal
        >
            {loading && (
                <div className="md-loading" role="status">
                    <ProgressSpinner strokeWidth="4" />
                    <span>Cargando…</span>
                </div>
            )}

            {!loading && forms && (
                <div className="receipt-forms">
                    <section className="receipt-forms__section">
                        <div className="receipt-forms__section-header">
                            <h3 className="md-section-title">Comprobante interno</h3>
                            {receipt && (
                                <Button
                                    text
                                    rounded
                                    icon="pi pi-download"
                                    className="md-icon-btn"
                                    aria-label="Descargar resumen"
                                    tooltip="Descargar resumen"
                                    tooltipOptions={{ position: 'left' }}
                                    loading={summary.downloadingId === receipt.administrativeForm}
                                    onClick={() => summary.download(receipt.administrativeForm, summaryFileName)}
                                />
                            )}
                        </div>
                        {receipt ? (
                            <div className="md-readonly-grid">
                                <ReadonlyField label="N° formulario">{receipt.formNumber}</ReadonlyField>
                                <ReadonlyField label="Fecha">{formatDateOnly(receipt.date)}</ReadonlyField>
                                <ReadonlyField label="Emisor">{receipt.billingEntityName}</ReadonlyField>
                                <ReadonlyField label="Destinatario">{receipt.customerName}</ReadonlyField>
                                <ReadonlyField label="Monto">{formatMoney(receipt.amount)}</ReadonlyField>
                                {receipt.pendingAmount > 0 && (
                                    <ReadonlyField label="Pendiente">{formatMoney(receipt.pendingAmount)}</ReadonlyField>
                                )}
                                <ReadonlyField label="Estado"><AdministrativeFormStateTag stateType={receipt.stateType} /></ReadonlyField>
                            </div>
                        ) : (
                            <p className="receipt-forms__empty">Sin comprobante interno.</p>
                        )}
                    </section>

                    <section className="receipt-forms__section">
                        <h3 className="md-section-title">{paymentsTitle}</h3>
                        {payments.length === 0 && (
                            <p className="receipt-forms__empty">Sin órdenes de pago.</p>
                        )}
                        {payments.map(payment => {
                            const appliesToSeveral = (payment.linkedReceiptsCount ?? 0) > 1;
                            return (
                                <div key={payment.administrativeForm} className="receipt-forms__card">
                                    {(appliesToSeveral || payment.paymentOrder) && (
                                        <div className="receipt-forms__card-header">
                                            {appliesToSeveral
                                                ? <span className="md-tag md-tag-mut">Aplica a {payment.linkedReceiptsCount} comprobantes</span>
                                                : <span />}
                                            {payment.paymentOrder && (
                                                <Button
                                                    text
                                                    rounded
                                                    icon="pi pi-download"
                                                    className="md-icon-btn"
                                                    aria-label={`Descargar orden de pago N° ${payment.formNumber ?? ''}`}
                                                    tooltip="Descargar orden de pago"
                                                    tooltipOptions={{ position: 'left' }}
                                                    loading={downloadingFormId === payment.administrativeForm}
                                                    onClick={() => handleDownload(payment.administrativeForm, payment.formNumber)}
                                                />
                                            )}
                                        </div>
                                    )}
                                    <div className="md-readonly-grid">
                                        <ReadonlyField label="Tipo">{payment.formTypeName ?? '—'}</ReadonlyField>
                                        <ReadonlyField label="N° formulario">{payment.formNumber}</ReadonlyField>
                                        <ReadonlyField label="Fecha">{formatDateOnly(payment.date)}</ReadonlyField>
                                        <ReadonlyField label="Emisor">{payment.billingEntityName}</ReadonlyField>
                                        <ReadonlyField label="Destinatario">{payment.customerName}</ReadonlyField>
                                        <ReadonlyField label="Monto">{formatMoney(payment.amount)}</ReadonlyField>
                                        <ReadonlyField label="Aplicado a este comprobante">{formatMoney(payment.appliedAmount)}</ReadonlyField>
                                        {payment.pendingAmount > 0 && (
                                            <ReadonlyField label="Pendiente">{formatMoney(payment.pendingAmount)}</ReadonlyField>
                                        )}
                                        <ReadonlyField label="Estado"><AdministrativeFormStateTag stateType={payment.stateType} /></ReadonlyField>
                                    </div>
                                </div>
                            );
                        })}
                    </section>
                </div>
            )}
        </Dialog>
    );
}
